import { promises as fs } from "node:fs";
import os from "node:os";
import path from "node:path";
import { fileURLToPath } from "node:url";

import type { Logger } from "pino";

import { applyMcpVault } from "./apply.js";

// Distinct from the roles seeder's `role-templates` and the brand seeder's
// `schema-templates` on purpose: in the installer bundle every module collapses
// into `dist/worker.js`, so all three seeders resolve `import.meta.url` to the
// same dir. A shared name would make the readers cross-pick each other's files.
const TEMPLATES_DIR_NAME = "mcp-server-templates";

// Vault layout (mirrors apply.ts defaults, but rooted at appostleHome so a
// custom $APPOSTLE_HOME is honoured).
const VAULT_DIR_NAME = "mcp-servers";
const MANAGED_STORE_NAME = "mcp-vault-managed.json";

// Per-server seed record: the set of catalog server names this machine has
// already been offered. A name is seeded at most once, ever — so a server the
// user later edits or deletes is never re-added, yet servers we ADD to the
// catalog in a future release are seeded on the next boot. Replaces the old
// boolean one-shot gate (which could never ship new servers to existing users).
const MANIFEST_NAME = ".mcp-vault-seeded.json";

// Legacy boolean sentinel written by the one-shot gate (≤ v0.0.65). Its mere
// presence means "an older build already seeded the catalog"; we use that to
// adopt the current catalog as the migration baseline so the per-server gate
// neither re-seeds nor re-adds anything the user removed.
const LEGACY_SENTINEL_NAME = ".mcp-vault-seeded";

interface SeedManifest {
  seeded: string[];
}

/**
 * Resolves the on-disk location of the bundled MCP server templates.
 *
 * Co-located with this file in both source
 * (`src/server/mcp-vault/mcp-server-templates`) and compiled output
 * (`dist/server/server/mcp-vault/mcp-server-templates`) — the build pipeline
 * copies the `.json` tree alongside the emitted `.js`, so a sibling lookup
 * works in dev (tsx) and prod (node dist). In the installer bundle the same
 * lookup resolves to `dist/mcp-server-templates` because `import.meta.url`
 * points to `dist/worker.js`.
 */
function resolveTemplatesDir(): string {
  const here = path.dirname(fileURLToPath(import.meta.url));
  return path.join(here, TEMPLATES_DIR_NAME);
}

interface SeedOptions {
  logger?: Logger;
  /** Override the environment used to resolve `{env:VAR}` placeholders (tests). */
  env?: Record<string, string | undefined>;
  /** Override the real home dir whose provider configs get written (tests). */
  homeDir?: string;
}

interface SeedResult {
  /** True when at least one new catalog server was copied into the vault this run. */
  seeded: boolean;
  /** File names newly copied into the vault this run. */
  copied: string[];
}

/** Read the per-server seed manifest, or null if it has never been written. */
async function readSeedManifest(manifestPath: string): Promise<string[] | null> {
  let raw: string;
  try {
    raw = await fs.readFile(manifestPath, "utf8");
  } catch {
    return null; // absent (or unreadable) — caller derives a migration baseline
  }
  try {
    const parsed = JSON.parse(raw) as Partial<SeedManifest>;
    if (Array.isArray(parsed.seeded)) {
      return parsed.seeded.filter((name): name is string => typeof name === "string");
    }
  } catch {
    // corrupt manifest — fall through to empty so we re-seed rather than wedge
  }
  return [];
}

async function pathExists(target: string): Promise<boolean> {
  try {
    await fs.access(target);
    return true;
  } catch {
    return false;
  }
}

/**
 * Seed Appostle's bundled MCP server catalog into the user's vault and apply
 * the current vault into whichever providers (Claude / OpenCode / Codex) are
 * installed.
 *
 * Per-server, seed-once. A `.mcp-vault-seeded.json` manifest records every
 * catalog name this machine has already been offered. On each boot only names
 * **not** in the manifest are copied into `<appostleHome>/mcp-servers/`
 * (creation-only). The vault itself is then applied on every boot so a synced
 * Dropbox hub can keep provider configs converged. This means:
 *   - a server we ADD to the catalog later lands on the user's next boot;
 *   - a server the user edits or deletes is never re-added (it stays in the
 *     manifest, so it's never "new" again);
 *   - user edits made in the vault propagate to provider configs.
 *
 * Migration: machines seeded by the older one-shot gate (≤ v0.0.65) have a
 * legacy boolean `.mcp-vault-seeded` sentinel but no manifest. Its presence
 * means the whole catalog was already offered, so we adopt the *current*
 * catalog as the baseline — nothing re-seeds, only future additions are new.
 *
 * `applyMcpVault` re-asserts the full vault on every boot. A user who hand-edits
 * one of OUR servers directly in the provider config (rather than in the vault
 * file) will see that edit reset. Edit the vault file, not the provider config,
 * for changes that should stick.
 *
 * Secrets and machine-specific paths are `{env:VAR}` placeholders. Every server
 * lands regardless: a set var resolves to its value, an unset one is written as
 * the literal and surfaces as failed for the user to wire up later.
 *
 * Best-effort: never throws. If apply fails the manifest is not advanced, so the
 * next boot retries; seeding must not block daemon startup.
 */
export async function seedMcpVault(
  appostleHome: string,
  options?: SeedOptions,
): Promise<SeedResult> {
  const logger = options?.logger;
  const manifestPath = path.join(appostleHome, MANIFEST_NAME);
  const legacySentinel = path.join(appostleHome, LEGACY_SENTINEL_NAME);

  const templatesDir = resolveTemplatesDir();
  let templates: string[];
  try {
    templates = (await fs.readdir(templatesDir)).filter((file) => file.endsWith(".json")).sort();
  } catch (error) {
    if ((error as NodeJS.ErrnoException).code === "ENOENT") {
      logger?.warn({ templatesDir }, "mcp server templates directory missing; skipping seed");
      return { seeded: false, copied: [] };
    }
    logger?.warn(
      { err: error, templatesDir },
      "failed to read mcp server templates; skipping seed",
    );
    return { seeded: false, copied: [] };
  }
  const catalogNames = templates.map((file) => file.slice(0, -".json".length));

  // Which catalog servers have already been offered on this machine?
  let seededNames = await readSeedManifest(manifestPath);
  if (seededNames == null) {
    // No manifest. If an older one-shot build already seeded (legacy sentinel
    // present), adopt the current catalog as the baseline so nothing re-seeds;
    // otherwise this is a fresh machine and everything is new.
    seededNames = (await pathExists(legacySentinel)) ? [...catalogNames] : [];
  }

  const seededSet = new Set(seededNames);
  const newNames = catalogNames.filter((name) => !seededSet.has(name));
  const nextManifest: SeedManifest = {
    seeded: [...new Set([...seededNames, ...catalogNames])].sort(),
  };

  const vaultDir = path.join(appostleHome, VAULT_DIR_NAME);

  try {
    await fs.mkdir(vaultDir, { recursive: true });

    if (newNames.length === 0) {
      // Nothing new — but still apply the current vault so a synced hub keeps
      // provider configs converged. Persist the manifest so a migrated/legacy
      // machine records its baseline and stops re-deriving it, then retire the
      // legacy sentinel.
      const result = await applyMcpVault({
        vaultDir,
        managedStorePath: path.join(appostleHome, MANAGED_STORE_NAME),
        homeDir: options?.homeDir ?? os.homedir(),
        env: options?.env,
        dryRun: false,
      });
      await writeSeedManifest(manifestPath, nextManifest);
      await fs.rm(legacySentinel, { force: true });

      const applied = result.providers
        .filter((provider) => provider.wrote)
        .map((provider) => ({
          provider: provider.provider,
          added: provider.added,
          updated: provider.updated,
          removed: provider.removed,
        }));
      if (applied.length > 0) {
        logger?.info({ vaultDir, applied }, "mcp vault applied");
      }
      return { seeded: false, copied: [] };
    }

    const copied: string[] = [];
    for (const name of newNames) {
      const file = `${name}.json`;
      const destination = path.join(vaultDir, file);
      try {
        // Creation-only: `wx` fails if the file already exists, so a server the
        // user has already authored / edited is never clobbered.
        const contents = await fs.readFile(path.join(templatesDir, file), "utf8");
        await fs.writeFile(destination, contents, { encoding: "utf8", flag: "wx" });
        copied.push(file);
      } catch (error) {
        if ((error as NodeJS.ErrnoException).code === "EEXIST") {
          continue;
        }
        throw error;
      }
    }

    const result = await applyMcpVault({
      vaultDir,
      managedStorePath: path.join(appostleHome, MANAGED_STORE_NAME),
      homeDir: options?.homeDir ?? os.homedir(),
      env: options?.env,
      dryRun: false,
    });

    // Advance the manifest only after a successful apply, so a failed apply is
    // retried on the next boot (the new names stay "new").
    await writeSeedManifest(manifestPath, nextManifest);
    await fs.rm(legacySentinel, { force: true });

    const applied = result.providers
      .filter((provider) => provider.wrote)
      .map((provider) => ({
        provider: provider.provider,
        added: provider.added,
        updated: provider.updated,
        removed: provider.removed,
      }));
    logger?.info(
      { vaultDir, copied: copied.length, newServers: newNames, applied },
      "mcp vault seeded",
    );

    return { seeded: true, copied };
  } catch (error) {
    logger?.warn({ err: error, vaultDir }, "mcp vault seed failed; will retry next boot");
    return { seeded: false, copied: [] };
  }
}

async function writeSeedManifest(manifestPath: string, manifest: SeedManifest): Promise<void> {
  await fs.writeFile(manifestPath, `${JSON.stringify(manifest, null, 2)}\n`, "utf8");
}
